<template>
    <v-row class="my-1" no-gutters>
        <v-col md="2">
            <p-account-search
                v-model="form.account_id"
                class="me-1"
                outlined
                :error="errorMessages.account_id.length > 0"
                :error-messages="errorMessages.account_id"
                :rules="[isRequired]"
                @input:object="$emit('account:object', $event)"
                @update:error="$emit('update:error')"
            >
            </p-account-search>
        </v-col>
        <v-col :md="isForeign ? 3 : 5">
            <v-text-field
                label="Narration"
                v-model="form.narration"
                :rules="[isRequired]"
                :disabled="!form.account_id"
                class="me-1"
                outlined
                dense
                background-color="white"
                hide-details="auto"
                @update:error="$emit('update:error')"
            >
            </v-text-field>
        </v-col>
        <v-col>
            <v-text-field
                :label="renderLabel('Debit - ' + baseCurrency.abbreviation)"
                v-model="form.base_debit"
                :rules="[...rules.numeric, ...rules.gtZero]"
                :disabled="!form.account_id"
                class="me-1"
                outlined
                flat
                dense
                hide-details="auto"
                background-color="white"
                @input="handleBaseDebitInput"
                @blur="form.base_debit = roundTwo(form.base_debit)"
                @update:error="$emit('update:error')"
            >
            </v-text-field>
        </v-col>
        <v-col>
            <v-text-field
                :label="renderLabel('Credit - ' + baseCurrency.abbreviation)"
                v-model="form.base_credit"
                :rules="[...rules.numeric, ...rules.gtZero]"
                :disabled="!form.account_id"
                class="me-1"
                outlined
                flat
                dense
                hide-details="auto"
                background-color="white"
                @blur="form.base_credit = roundTwo(form.base_credit)"
                @input="handleBaseCreditInput"
                @update:error="$emit('update:error')"
            >
            </v-text-field>
        </v-col>
        <v-col v-if="isForeign">
            <v-text-field
                :label="renderLabel('Debit - ' + journalCurrency.abbreviation)"
                v-model="form.trans_debit"
                :rules="[...rules.numeric, ...rules.gtZero]"
                :disabled="!form.account_id"
                outlined
                class="me-1"
                flat
                dense
                hide-details="auto"
                background-color="white"
                @blur="form.trans_debit = roundTwo(form.trans_debit)"
                @input="handleTransDebitInput"
                @update:error="$emit('update:error')"
            >
            </v-text-field>
        </v-col>
        <v-col v-if="isForeign">
            <v-text-field
                :label="renderLabel('Credit - ' + journalCurrency.abbreviation)"
                v-model="form.trans_credit"
                :rules="[...rules.numeric, ...rules.gtZero]"
                :disabled="!form.account_id"
                class="me-1"
                outlined
                flat
                dense
                hide-details="auto"
                background-color="white"
                @blur="form.trans_credit = roundTwo(form.trans_credit)"
                @input="handleTransCreditInput"
                @update:error="$emit('update:error')"
            >
            </v-text-field>
        </v-col>
        <v-col class="d-flex" md="2">
            <p-dimension-search
                v-model="form.dimension_id"
                :disabled="!form.account_id"
                @tab="$emit('tab:last-item', index)"
                outlined
                hide-details="auto"
                @input:object="dimension => form.dimension = dimension && dimension.name ? dimension.name : null"
                @update:error="$emit('update:error')"
            >
            </p-dimension-search>
            <p-icon-btn
                icon="mdi-delete"
                tooltip="Delete"
                @click="$emit('click:delete', form.key)"
            ></p-icon-btn>
        </v-col>
    </v-row>
</template>

<script>
// Third Parties
import { round, isEmpty } from "lodash";
// Services
// Components
import PIconBtn from "../common/PIconBtn";
import PAccountSearch from "./../searchComponents/PAccountSearch";
import PDimensionSearch from "./../searchComponents/PDimensionSearch";
// Utils
import { numeric, gtZero, isRequired } from "./../../utils/validations";

export default {
    components: { PIconBtn, PAccountSearch, PDimensionSearch },
    name: "JournalEntryLine",
    props: {
        index: Number,
        value: Object,
        baseCurrency: Object,
        journalCurrency: Object,
        exchangeRates: Array,
        isTotalsRow: Boolean,
        unallowedSelectedAccountsIds: Array,
        currentlySelectedAccountsObjects: Array,
    },
    data() {
        return {
            rules: { numeric, gtZero },
            errorMessages: {
                account_id: [],
            },
            form: { ...this.value },
        };
    },
    computed: {
        showLabel() {
            return this.index == 0;
        },
        isForeign() {
            return this.baseCurrency != this.journalCurrency;
        },
        /**
         * @todo proper handling of undefined || null state instead of just returning 1.
         */
        transRate() {
            if (this.exchangeRates === undefined) return 1;
            let exchangeRate = this.exchangeRates.find(
                (each) => each.currency_id == this.journalCurrency.id
            );
            if (exchangeRate === undefined) return 1;
            return parseFloat(exchangeRate.customRate);
        },
        /**
         * The exchange rate for the account depending on currency.
         */
        accountRate() {
            if (!this.exchangeRates)
                throw new Error(
                    "Exchange rate undefined! cannot calculate account rate."
                );
            if (!this.accountObject.currency_id)
                throw new Error(
                    "Account Object Undefined! cannot calculate account rate."
                );
            let exchangeRate = this.exchangeRates.find(
                (each) => each.currency_id == this.accountObject.currency_id
            );
            if (!exchangeRate)
                throw new Error("Could not resove exchange rate");
            return parseFloat(exchangeRate.customRate);
        },
        /**
         * The account object for the v-model form.account_id.
         * It is needed to get the currency of the account, therefore it is possible
         * to calculate the account debit/credit from exchange rate.
         */
        accountObject() {
            if (isEmpty(this.currentlySelectedAccountsObjects)) return {};
            else
                return this.currentlySelectedAccountsObjects.find(
                    (each) => each.id == this.form.account_id
                );
        },
        accountName() {
            if (isEmpty(this.accountObject)) return null;
            return this.accountObject.displayName;
        },
    },
    watch: {
        exchangeRates: {
            handler() {
                this.recalculateForeignAmount();
                this.recalculateAccountAmount();
            },
            deep: true,
        },
        journalCurrency() {
            this.recalculateForeignAmount();
        },
        /**
         * @todo Add documentation for this function
         */
        unallowedSelectedAccountsIds() {
            this.errorMessages.account_id = [];
            let unallowedAccount = this.unallowedSelectedAccountsIds.includes(
                this.form.account_id
            );
            if (unallowedAccount)
                this.errorMessages.account_id = ["Missing Exchange Rate!"];
        },
        form: {
            handler(val) {
                this.recalculateAccountAmount();
                this.$emit("input", {
                    ...val,
                    debit: this.form.debit,
                    credit: this.form.credit,
                    account: this.accountName,
                });
            },
            deep: true,
        },
        "form.account_id": {
            handler() {
                this.recalculateAccountAmount();
                this.$emit("input", {
                    ...this.form,
                    debit: this.form.debit,
                    credit: this.form.credit,
                    account: this.accountName,
                });
            },
        },
    },
    methods: {
        round,
        isRequired,
        roundTwo(number) {
            if (number === null || number === undefined) return null;
            return round(number, 2);
        },
        renderLabel(label) {
            return this.showLabel ? label : null;
        },
        /**
         * @param {number} value The amount need to be converted.
         * @param {number} rate The exchange rate
         * @todo The isNaN(parseFloat(value)) is not clean and should handled in a proper way.
         * @todo This function and calculateBase function seems duplicate. A utilily function must
         * be created instead to convert between currencies given the exchange rates.
         * @return {float} Amount in foreign currency.
         */
        calculateForeign(value, rate) {
            if (isNaN(parseFloat(value))) return null;
            return round(parseFloat(value) / parseFloat(rate), 2);
        },
        calculateBase(value, rate) {
            if (isNaN(parseFloat(value))) return null;
            return round(parseFloat(value) * parseFloat(rate), 2);
        },
        handleBaseDebitInput(value) {
            this.resetCredit();
            this.form.trans_debit = this.calculateForeign(
                value,
                this.transRate
            );
        },
        handleBaseCreditInput(value) {
            this.resetDebit();
            this.form.trans_credit = this.calculateForeign(
                value,
                this.transRate
            );
        },
        handleTransDebitInput(value) {
            this.resetCredit();
            this.form.base_debit = this.calculateBase(value, this.transRate);
        },
        handleTransCreditInput(value) {
            this.resetDebit();
            this.form.base_credit = this.calculateBase(value, this.transRate);
        },
        recalculateForeignAmount() {
            if (this.form.base_debit > 0)
                this.handleBaseDebitInput(this.form.base_debit);
            else if (this.form.base_credit > 0)
                this.handleBaseCreditInput(this.form.base_credit);
        },
        recalculateAccountAmount() {
            this.form.debit = this.calculateForeign(
                this.form.base_debit,
                this.accountRate
            );
            this.form.credit = this.calculateForeign(
                this.form.base_credit,
                this.accountRate
            );
        },
        resetDebit() {
            this.form.base_debit = null;
            this.form.trans_debit = null;
            this.form.debit = null;
        },
        resetCredit() {
            this.form.base_credit = null;
            this.form.trans_credit = null;
            this.form.credit = null;
        },
        handleDimensionInputObject() {
            alert("Name")
        }
    },
    mounted() {
        this.form.trans_debit = this.calculateForeign(
            this.form.base_debit,
            this.transRate
        );
        this.form.trans_credit = this.calculateForeign(
            this.form.base_credit,
            this.transRate
        );
        this.$emit("input", this.form);
    },
};
</script>
